import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useAllCostCenters } from '~/data/costCenters';
import { EmployeeCostCenter } from '~/data/employee';
import { useAllEmployees } from '~/data/employees';
import { Enum, useAllEnums } from '~/data/enums';
import { getLegalEntityName, useAllLegalEntities } from '~/data/legalEntity';
import { useMyUnits } from '~/data/orgunits';

import { TableDataColumn, TableHeaderColumn } from '../TableDataComponents';

import { EmplHistoryType } from './data';
import { EmploymentHistorySxStyles } from './EmploymentHistoryStyles';

export type dataType = {
  data: EmplHistoryType[];
};

export const EmploymentHistory = (props: dataType) => {
  const { data: emplHistoryData } = props;
  const { t } = useTranslation();
  const sxStyles = EmploymentHistorySxStyles();
  const { allEnums } = useAllEnums();
  const { allEmployees } = useAllEmployees();
  const { costCenters } = useAllCostCenters();
  const { legalEntities } = useAllLegalEntities();

  const { myUnits } = useMyUnits();

  const getEnumName = (enumValue: Enum[], id: string) => {
    if (id === undefined || id === '') return '';
    const specificEnum = enumValue.find((en) => en.code === id);
    return specificEnum === undefined ? id : specificEnum.name;
  };

  const orgUnit = (id: number | undefined) => {
    if (id === undefined) return '';
    const unit = myUnits.find((unit) => unit.fOrgUnitId === id);
    return unit === undefined
      ? ''
      : `${unit.fOrgUnitName} (${unit.fOrgUnitNumber})`;
  };

  const getCostCenter = (ecc: EmployeeCostCenter | undefined) => {
    if (ecc === undefined) return '';
    const cc = costCenters.find(
      (cc) => cc.fCostCenterId === ecc.fEmpCostCenter,
    );
    const ccPercent = ecc.fEmpCostCenterPercent
      ? `(${ecc.fEmpCostCenterPercent}%)`
      : '';
    return cc === undefined
      ? ''
      : `
      ${cc.fCostCenterCode} ${cc.fCostCenterName} ${ccPercent}`;
  };

  const getAdditionalCostCenter = (e: EmployeeCostCenter[] | undefined) => {
    if (e === undefined) return '';
    const cc = e.map((val) => getCostCenter(val));
    return cc.map((val, index) => (index > 0 ? val : ''));
  };

  const getManagerName = (e: number) => {
    return allEmployees[e]
      ? `${allEmployees[e].fEmpLastName}, ${allEmployees[e].fEmpFirstName}`
      : '';
  };

  return (
    <>
      <Table cellSpacing="0" cellPadding="0" sx={sxStyles.tableHeader}>
        <TableHead>
          <TableRow>
            <TableHeaderColumn title={t('laStartDate')} />
            <TableHeaderColumn title={t('laEndDate')} />
            <TableHeaderColumn title={t('laPosition')} />
            <TableHeaderColumn title={t('laBusinessTitle')} />
            <TableHeaderColumn title={t('laOrgUnits')} />
            <TableHeaderColumn title={t('laCC')} />
            <TableHeaderColumn title={t('laACC')} />
            <TableHeaderColumn title={t('laLegalEntity')} />
            <TableHeaderColumn title={t('laManager')} />
            <TableHeaderColumn title={t('laInfo')} />
          </TableRow>
        </TableHead>
        <TableBody>
          {emplHistoryData.length > 0 ? (
            emplHistoryData.map((e: EmplHistoryType, index: number) => {
              return (
                <TableRow key={index}>
                  <TableDataColumn
                    title={moment(e.fInternalPositionStartDate).format(
                      'DD.MM.YYYY',
                    )}
                    first={true}
                  />
                  <TableDataColumn
                    title={moment(e.fInternalPositionEndDate).format(
                      'DD.MM.YYYY',
                    )}
                  />
                  <TableDataColumn
                    title={getEnumName(
                      allEnums.EMPLOYEE_POSITION,
                      e.fInternalPositionPosition,
                    )}
                  />
                  <TableDataColumn title={e.fEmpJobTitle} />
                  <TableDataColumn title={orgUnit(e.fInternalPositionUnit)} />
                  <TableDataColumn
                    title={getCostCenter(
                      e.fEmpCostCenters ? e.fEmpCostCenters[0] : undefined,
                    )}
                  />
                  <TableDataColumn
                    title={getAdditionalCostCenter(
                      e.fEmpCostCenters ? e.fEmpCostCenters : undefined,
                    )}
                  />
                  <TableDataColumn
                    title={getLegalEntityName(e.fEmpLegalEntity, legalEntities)}
                  />
                  <TableDataColumn
                    title={getManagerName(e.fInternalPositionManager)}
                  />
                  <TableDataColumn title={e.fInternalPositionInfo} />
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableDataColumn first={true} title={`${t('laNoHistoryNow')}`} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};
